import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/LoginOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import MedicalIcon from "@mui/icons-material/MedicalInformationOutlined";
import FamilyIcon from "@mui/icons-material/Diversity1Outlined";
import VideocamOffIcon from "@mui/icons-material/VideocamOffOutlined";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCameraOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  reduceDisplayName,
  type DoorSignIdentityMode,
  type DoorSignStatus,
  type VideoCallSeat,
  type VideoCallSeatSummary,
} from "@shared/types/foyer";
import {
  foyerApi,
  type CareAuditFilter,
  type CareAuditRecord,
  type CareAuditVerification,
  type CareOccupancy,
  type CarePhiReveal,
  type PhotoMailboxInfo,
} from "../http/foyer";
import CollapsiblePanel from "./CollapsiblePanel";
import CareProfileDialog from "./CareProfileDialog";
import FamilyContactsDialog from "./FamilyContactsDialog";
import ResidentPhotoMailboxPanel from "./ResidentPhotoMailboxPanel";
import { PageHeader } from "./PageHeader";
import { PermissionGate, usePermission, type PlaceRef } from "../auth/access";
import { describeError, useToast } from "./toast";
import type { ActionKey } from "./toast/ToastProvider";
import { Trans, useTranslation } from "react-i18next";
import { errorText } from "../utils/apiError";
import { useFormat } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

interface Props {
  premiseId: string;
  rooms: { id: string; name?: string }[];
  /** Current premise-wide identity policy (from the draft config). */
  identityMode: DoorSignIdentityMode;
  /** Mirror the policy change into the draft so a later full Save can't clobber
   *  the value the dedicated endpoint just persisted. */
  onIdentityModeChange: (mode: DoorSignIdentityMode) => void;
  /** Changing the policy decrypts + rebuilds resident names, so it needs PHI access. */
  canViewPhi: boolean;
  /** Premise timezone - prefilled into a family contact's quiet hours (VC-11). */
  premiseTimezone?: string;
  /**
   * Admitting fills a room, discharging empties it - the server already flips
   * `Room.occupied` as part of both. This mirrors that into the premise draft
   * so the Rooms tab isn't showing yesterday's answer until a page reload.
   */
  onRoomOccupancyChange?: (roomId: string, occupied: boolean) => void;
}

const STATUS_OPTIONS: DoorSignStatus[] = [
  "none",
  "resting",
  "knock",
  "do-not-disturb",
  "out",
];

const SAMPLE_NAME = "Astrid Lund";

const IDENTITY_OPTIONS: DoorSignIdentityMode[] = [
  "none",
  "initials",
  "first-name",
  "first-initial",
  "full-name",
];

/** Privacy ordering - confirm before moving to a MORE exposing policy. */
const EXPOSURE_RANK: Record<DoorSignIdentityMode, number> = {
  none: 0,
  initials: 1,
  "first-name": 2,
  "first-initial": 3,
  "full-name": 4,
};

const PHI_CHIP = "PHI";
const NO_VALUE = "-";

function statusLabel(t: Translate, status: DoorSignStatus): string {
  return translateKey(t, `care:residentsEditor.doorSignStatus.${status}`, {
    defaultValue: status,
  });
}

function identityModeName(t: Translate, mode: DoorSignIdentityMode): string {
  return translateKey(t, `care:residentsEditor.identityMode.${mode}`, {
    defaultValue: mode,
  });
}

function identityOptionLabel(t: Translate, mode: DoorSignIdentityMode): string {
  if (mode === "none") return t("care:residentsEditor.identityOptionNone");
  return t("care:residentsEditor.identityOption", {
    mode: identityModeName(t, mode),
    sample: reduceDisplayName(SAMPLE_NAME, mode),
  });
}

/**
 * Care authoring: admit/discharge residents, set per-room door-sign status, and
 * set the premise-wide identity policy. The roster is PHI-free - it shows only
 * the policy-reduced display name (often blank). A name typed at admit is
 * encrypted server-side and never read back here.
 */
export default function ResidentsEditor({
  premiseId,
  rooms,
  identityMode,
  onIdentityModeChange,
  canViewPhi,
  premiseTimezone,
  onRoomOccupancyChange,
}: Props) {
  const [roster, setRoster] = useState<CareOccupancy[]>([]);
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const place: PlaceRef = useMemo(() => ({ premiseId }), [premiseId]);
  const mayEditResidents = usePermission("residents", "edit", place);
  const mayViewPhi = usePermission("patient-data", "view", place);
  const mayViewCalls = usePermission("video-calls", "view", place);
  const mayEditCalls = usePermission("video-calls", "edit", place);
  const mayAdministrateCalls = usePermission(
    "video-calls",
    "administrate",
    place
  );
  const mayViewAudit = usePermission("audit-log", "view", place);
  const showIdentity = canViewPhi && mayViewPhi;
  const [busy, setBusy] = useState(false);

  // Care-profile editor (clinical whiteboard) - opened per resident row.
  const [profileFor, setProfileFor] = useState<CareOccupancy | null>(null);

  // Family contacts (VC-11) - opened per resident row, and automatically right
  // after an admit, because adding the daughter is part of admitting the mother.
  const [familyFor, setFamilyFor] = useState<CareOccupancy | null>(null);
  // personRef → how many contacts, and how many are still waiting for consent.
  // One list call for the whole roster rather than one per row; the list view is
  // masked, so this costs no PHI disclosure.
  const [familyCounts, setFamilyCounts] = useState<
    Record<string, { total: number; pending: number }>
  >({});
  // The directory needs its own permission. Rather than guess at the caller's
  // grants, we ask once: if the list is refused, the button is not offered.
  const [canManageFamily, setCanManageFamily] = useState(true);

  // Family photos - one mailbox per RESIDENT (not per room), opened per row.
  // personRef → mailbox, for the row's on/off indicator.
  const [photosFor, setPhotosFor] = useState<CareOccupancy | null>(null);
  const [mailboxes, setMailboxes] = useState<Record<string, PhotoMailboxInfo>>(
    {}
  );

  // Video-calling SEATS (VC2-6) - the commercial entitlement, separate from the
  // resident's privacy opt-out next to it. personRef → the active seat, so a
  // row can say not just "on" but *who is paying for it*.
  const [seats, setSeats] = useState<Record<string, VideoCallSeat>>({});
  const [seatSummary, setSeatSummary] = useState<VideoCallSeatSummary | null>(
    null
  );
  // Same reasoning as `canManageFamily`: assigning a seat needs
  // `canManageCallContacts`, so a 403 on the roster is an answer, not a fault -
  // hide the column rather than show a control that will always fail.
  const [canManageSeats, setCanManageSeats] = useState(true);
  const showSeats = canManageSeats && mayViewCalls;
  // Per-row, not the global `busy`: assigning one resident's seat is a network
  // round-trip and must not freeze the discharge button on every other row.
  const [seatBusy, setSeatBusy] = useState<string | null>(null);

  // Admit form (lives in a modal dialog)
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitRoom, setAdmitRoom] = useState("");
  const [admitName, setAdmitName] = useState("");
  const [admitDob, setAdmitDob] = useState("");
  const [admitMrn, setAdmitMrn] = useState("");
  const [admitEncounter, setAdmitEncounter] = useState("");

  // PHI reveal - keyed by personRef. Each reveal is an audited server call, so
  // we only fetch on explicit operator action and cache the result per row.
  const [revealed, setRevealed] = useState<Record<string, CarePhiReveal>>({});
  const [revealing, setRevealing] = useState<string | null>(null);

  // Åtkomstlogg panel (canViewPhi only). The filters here are not a convenience
  // - they ARE the compliance deliverable: PDL 4 kap. requires the care provider
  // to be able to produce "who accessed resident X between these dates" without
  // asking their software vendor for a database dump.
  const [audit, setAudit] = useState<CareAuditRecord[] | null>(null);
  const [auditPhiOnly, setAuditPhiOnly] = useState(true);
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditSubject, setAuditSubject] = useState("");
  const [auditTruncated, setAuditTruncated] = useState(false);
  const [auditChain, setAuditChain] = useState<CareAuditVerification | null>(
    null
  );
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  /** Per-resident family-contact counts for the roster badges. */
  const refreshFamilyCounts = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        scope: "person",
      });
      const next: Record<string, { total: number; pending: number }> = {};
      for (const c of rows) {
        if (!c.personRef || c.revokedAt) continue;
        const bucket = (next[c.personRef] ??= { total: 0, pending: 0 });
        bucket.total += 1;
        if (!c.consent) bucket.pending += 1;
      }
      setFamilyCounts(next);
      setCanManageFamily(true);
    } catch {
      // A 403 here is a permissions answer, not an error worth shouting about:
      // this operator manages the roster but not the call directory.
      setFamilyCounts({});
      setCanManageFamily(false);
    }
  }, [premiseId]);

  /**
   * The premise's calling seats: who holds one, and the pool ceiling the
   * operator is working under. Two calls for the whole roster rather than one
   * per row, and both are PHI-free.
   *
   * The summary is session-gated while the roster needs
   * `canManageCallContacts`, so they fail independently: an operator without
   * the permission still gets no column, and a premise that has simply bought
   * nothing still gets an honest "no seats bought" instead of a silent switch.
   */
  const refreshSeats = useCallback(async () => {
    try {
      const rows = await foyerApi.listVideoCallSeats(premiseId);
      const next: Record<string, VideoCallSeat> = {};
      for (const s of rows) {
        if (s.status === "active") next[s.personRef] = s;
      }
      setSeats(next);
      setCanManageSeats(true);
    } catch {
      setSeats({});
      setCanManageSeats(false);
      return;
    }
    try {
      setSeatSummary(await foyerApi.videoCallSeatSummary(premiseId));
    } catch {
      // The ceiling is unknown; the roster above is still true. Leave the last
      // known summary rather than blanking the counts we just rendered.
    }
  }, [premiseId]);

  /** The premise's photo mailboxes, keyed by resident. */
  const refreshMailboxes = useCallback(async () => {
    try {
      const rows = await foyerApi.listPhotoMailboxes(premiseId);
      const next: Record<string, PhotoMailboxInfo> = {};
      for (const mb of rows) if (mb.personRef) next[mb.personRef] = mb;
      setMailboxes(next);
    } catch {
      setMailboxes({}); // non-fatal: the indicator just won't show
    }
  }, [premiseId]);

  const refresh = useCallback(async () => {
    void refreshFamilyCounts();
    void refreshSeats();
    void refreshMailboxes();
    try {
      setRoster(await foyerApi.listOccupancy(premiseId));
      // Collapse any revealed identities - the roster changed and a discharged
      // resident's PHI should not linger on screen.
      setRevealed({});
    } catch (err) {
      toast.error(describeError(t, "loadResidents", err));
    }
  }, [
    premiseId,
    refreshFamilyCounts,
    refreshSeats,
    refreshMailboxes,
    toast,
    t,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const occupiedRoomIds = useMemo(
    () => new Set(roster.map((o) => o.roomId)),
    [roster]
  );
  const freeRooms = useMemo(
    () => rooms.filter((r) => !occupiedRoomIds.has(r.id)),
    [rooms, occupiedRoomIds]
  );
  const roomLabel = useCallback(
    (id: string) => {
      const r = rooms.find((x) => x.id === id);
      return r?.name ? `${r.name} (${id})` : id;
    },
    [rooms]
  );

  /**
   * Run one action: `success` is the toast on success (it names the room),
   * `failure` the action key of the error toast, with its params.
   */
  async function run(
    success: string,
    failure: { key: ActionKey; params?: Record<string, string> },
    fn: () => Promise<unknown>
  ) {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      await refresh();
    } catch (err) {
      toast.error(describeError(t, failure.key, err, failure.params));
    } finally {
      setBusy(false);
    }
  }

  const resetAdmitForm = () => {
    setAdmitRoom("");
    setAdmitName("");
    setAdmitDob("");
    setAdmitMrn("");
    setAdmitEncounter("");
  };

  const closeAdmit = () => {
    setAdmitOpen(false);
    resetAdmitForm();
  };

  const admit = () => {
    if (!admitRoom || !admitName.trim()) return;
    const roomId = admitRoom;
    void run(
      t("residentsEditor.admitted", { room: roomLabel(roomId) }),
      { key: "admitResident", params: { room: roomLabel(roomId) } },
      async () => {
        const created = await foyerApi.admitResident(premiseId, {
          roomId: admitRoom,
          person: {
            name: admitName.trim(),
            dob: admitDob.trim() || null,
            mrn: admitMrn.trim() || null,
          },
          externalEncounterId: admitEncounter.trim() || null,
        });
        // The admit succeeded, so the room is occupied server-side.
        onRoomOccupancyChange?.(roomId, true);
        setAdmitOpen(false);
        resetAdmitForm();
        // Hand straight over to the family contacts rather than leaving it as a
        // second errand: "add the daughter" is part of the admission conversation
        // that is happening right now, and it is the one moment staff have the
        // relative's number in front of them.
        if (canManageFamily && created?.personRef) setFamilyFor(created);
      }
    );
  };

  const discharge = (occ: CareOccupancy) => {
    // Name the consequence that is not obvious from the word "discharge": the
    // family contacts go with the stay (VC-06). Staff who learn that from the
    // empty TV afterwards learn it too late.
    const family = familyCounts[occ.personRef]?.total ?? 0;
    const room = roomLabel(occ.roomId);
    const question = family
      ? t("residentsEditor.dischargeConfirmWithFamily", {
          room,
          count: family,
        })
      : t("residentsEditor.dischargeConfirm", { room });
    if (!window.confirm(question)) return;
    void run(
      t("residentsEditor.discharged", { room }),
      { key: "dischargeResident", params: { room } },
      async () => {
        await foyerApi.dischargeResident(premiseId, occ.id);
        onRoomOccupancyChange?.(occ.roomId, false);
      }
    );
  };

  const setStatus = (occ: CareOccupancy, status: DoorSignStatus) => {
    void run(
      t("residentsEditor.doorSignSet", {
        room: roomLabel(occ.roomId),
        status: statusLabel(t, status),
      }),
      { key: "setDoorSignStatus", params: { room: roomLabel(occ.roomId) } },
      () => foyerApi.setDoorSignStatus(premiseId, occ.roomId, status)
    );
  };

  // Flip the resident-level video-call opt-out straight from the roster (VC-30
  // §4.8) - the privacy control must be reachable here, not only buried inside
  // the clinical care-profile dialog. `videoCallsDisabled` is one OPTIONAL field
  // on a full-PUT profile, so we read the current profile first and re-send it
  // with just that bit flipped; a partial save would blank the clinical fields.
  const setVideoCallsDisabled = (occ: CareOccupancy, disabled: boolean) => {
    const room = roomLabel(occ.roomId);
    void run(
      disabled
        ? t("residentsEditor.videoCallsOff", { room })
        : t("residentsEditor.videoCallsOn", { room }),
      {
        key: disabled
          ? "turnOffResidentVideoCalls"
          : "turnOnResidentVideoCalls",
        params: { room },
      },
      async () => {
        const p = await foyerApi.getCareProfile(premiseId, occ.personRef);
        await foyerApi.saveCareProfile(premiseId, occ.personRef, {
          allergies: p.allergies,
          isolation: p.isolation,
          fallRisk: p.fallRisk,
          diet: p.diet,
          precautions: p.precautions,
          careTeam: p.careTeam,
          notes: p.notes,
          videoCallsDisabled: disabled,
        });
      }
    );
  };

  /**
   * Assign or release this resident's calling seat (VC2-6).
   *
   * Deliberately NOT routed through `run()`: that spins the shared `busy` flag,
   * which disables every control on every row. A seat toggle is one resident's
   * business, so it takes its own per-row spinner and leaves the rest of the
   * roster usable.
   */
  const setSeatAssigned = async (occ: CareOccupancy, assign: boolean) => {
    const seat = seats[occ.personRef];
    // Releasing a FAMILY seat cancels something a relative is being charged
    // for - and the operator cannot give it back, only the family can re-buy
    // it. That asymmetry has to be stated before the click, not after.
    if (!assign && seat?.source === "family") {
      const ok = window.confirm(
        t("residentsEditor.familySeatConfirm", { premise: premiseId })
      );
      if (!ok) return;
    }
    setSeatBusy(occ.personRef);
    const where = roomLabel(occ.roomId);
    try {
      if (assign) {
        const res = await foyerApi.assignVideoCallSeat(
          premiseId,
          occ.personRef
        );
        toast.success(
          res.alreadyActive
            ? t("residentsEditor.seatAlready", { room: where })
            : t("residentsEditor.seatAssigned", { room: where })
        );
      } else {
        await foyerApi.releaseVideoCallSeat(premiseId, occ.personRef);
        toast.success(t("residentsEditor.seatReleased", { room: where }));
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      toast.error(
        code === "pool_full"
          ? t("residentsEditor.poolFull")
          : describeError(
              t,
              assign ? "assignCallingSeat" : "releaseCallingSeat",
              err,
              { room: where }
            ),
        code === "pool_full" ? { durationMs: 12000 } : undefined
      );
    } finally {
      setSeatBusy(null);
      // Re-read either way: a failed assign still moves the free-seat count if
      // someone else was assigning at the same time.
      await refreshSeats();
    }
  };

  const changeIdentityMode = (mode: DoorSignIdentityMode) => {
    if (mode === identityMode) return;
    // Confirm before raising public exposure.
    if (EXPOSURE_RANK[mode] > EXPOSURE_RANK[identityMode] && mode !== "none") {
      const ok = window.confirm(
        t("residentsEditor.identityConfirm", {
          sample: reduceDisplayName(SAMPLE_NAME, mode),
        })
      );
      if (!ok) return;
    }
    void run(
      t("residentsEditor.identityChanged", {
        mode: identityModeName(t, mode),
      }),
      { key: "changeDoorSignIdentityPolicy" },
      async () => {
        await foyerApi.setDoorSignIdentityMode(premiseId, mode);
        onIdentityModeChange(mode);
      }
    );
  };

  // Reveal one resident's identity. This is an audited PHI read on the server,
  // so it only fires on an explicit click and the result is cached per row.
  const reveal = (occ: CareOccupancy) => {
    if (revealed[occ.personRef] || revealing) return;
    setRevealing(occ.personRef);
    foyerApi
      .revealPerson(premiseId, occ.personRef)
      .then((phi) => setRevealed((prev) => ({ ...prev, [occ.personRef]: phi })))
      .catch((err) =>
        toast.error(
          describeError(t, "revealResident", err, {
            room: roomLabel(occ.roomId),
          })
        )
      )
      .finally(() => setRevealing(null));
  };

  const hide = (personRef: string) =>
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[personRef];
      return next;
    });

  const identityDetails = (phi: CarePhiReveal) =>
    phi.dob && phi.mrn
      ? t("residentsEditor.identityDetails.both", {
          dob: phi.dob,
          mrn: phi.mrn,
        })
      : phi.dob
        ? t("residentsEditor.identityDetails.dob", { dob: phi.dob })
        : phi.mrn
          ? t("residentsEditor.identityDetails.mrn", { mrn: phi.mrn })
          : t("residentsEditor.identityDetails.none");

  /** The current filter set, shared by the table, the CSV link and verification. */
  const auditFilter = useMemo(
    (): CareAuditFilter => ({
      phiOnly: auditPhiOnly,
      // `date` inputs give a local calendar day; widen `to` to the END of that
      // day so "1 Jan → 31 Jan" includes the 31st. An off-by-one-day export is a
      // wrong answer to a regulator, not a cosmetic bug.
      from: auditFrom ? new Date(`${auditFrom}T00:00:00`).toISOString() : "",
      to: auditTo
        ? new Date(
            new Date(`${auditTo}T00:00:00`).getTime() + 24 * 60 * 60 * 1000
          ).toISOString()
        : "",
      subjectRef: auditSubject.trim(),
    }),
    [auditPhiOnly, auditFrom, auditTo, auditSubject]
  );

  const loadAudit = useCallback(
    async (filter: CareAuditFilter) => {
      setAuditLoading(true);
      setAuditError(null);
      try {
        const page = await foyerApi.listCareAudit(premiseId, {
          ...filter,
          limit: 500,
        });
        setAudit(page.rows);
        setAuditTruncated(page.truncated);
        // Verify the chain on every load rather than behind another button: a
        // tamper indicator nobody clicks is not a control.
        setAuditChain(
          await foyerApi.verifyCareAudit(premiseId, {
            from: filter.from || undefined,
            to: filter.to || undefined,
          })
        );
      } catch (err) {
        setAuditError(errorText(err, t));
      } finally {
        setAuditLoading(false);
      }
    },
    [premiseId, t]
  );

  const familyButtonLabel = (counts?: { total: number; pending: number }) => {
    if (!counts?.total) return t("residentsEditor.family");
    return counts.pending > 0
      ? t("residentsEditor.familyWithPending", {
          count: counts.total,
          pending: counts.pending,
        })
      : t("residentsEditor.familyWithCount", { count: counts.total });
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("residentsEditor.eyebrow")}
        title={t("residentsEditor.title")}
      />

      {/* Identity policy */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("residentsEditor.identityPolicy")}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl
              size="small"
              sx={{ minWidth: 320 }}
              disabled={!canViewPhi || !mayViewPhi || !mayEditResidents || busy}
            >
              <InputLabel id="identity-mode">
                {t("residentsEditor.showOnDoorSigns")}
              </InputLabel>
              <Select
                labelId="identity-mode"
                label={t("residentsEditor.showOnDoorSigns")}
                value={identityMode}
                onChange={(e) =>
                  changeIdentityMode(e.target.value as DoorSignIdentityMode)
                }
                data-testid="identity-mode-select"
              >
                {IDENTITY_OPTIONS.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {identityOptionLabel(t, mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!canViewPhi && (
              <Typography variant="caption" color="text.secondary">
                {t("residentsEditor.requiresPhi")}
              </Typography>
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            <Trans
              t={t}
              i18nKey="residentsEditor.identityHelp"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </CardContent>
      </Card>

      {/* Roster */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">
              {t("residentsEditor.currentResidents")}
            </Typography>
            <PermissionGate resource="residents" level="edit" place={place}>
              <PermissionGate
                resource="patient-data"
                level="edit"
                place={place}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LoginIcon />}
                  onClick={() => setAdmitOpen(true)}
                  data-testid="admit-open"
                >
                  {t("residentsEditor.admitResident")}
                </Button>
              </PermissionGate>
            </PermissionGate>
          </Stack>
          {/* The ceiling the operator is working under, stated before they
              reach for a switch - "why is this greyed out" should never need a
              trip to another page to answer. Also names the second condition:
              a seat alone does not put the tile on a resident's TV if their
              own opt-out is off. */}
          {showSeats && seatSummary && roster.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
              data-testid="calling-seat-pool"
            >
              <Trans
                t={t}
                i18nKey="residentsEditor.poolLine"
                values={{ pool: poolCopy(t, seatSummary) }}
                components={{ em: <em /> }}
              />
            </Typography>
          )}
          {roster.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("residentsEditor.empty")}
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" data-testid="roster-table">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("residentsEditor.columns.unit")}</TableCell>
                    <TableCell>
                      {t("residentsEditor.columns.shownOnSign")}
                    </TableCell>
                    {showIdentity && (
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {t("residentsEditor.columns.identity")}
                          <Tooltip
                            title={t("residentsEditor.columns.identityHint")}
                          >
                            <GavelIcon
                              fontSize="inherit"
                              color="action"
                              sx={{ fontSize: 14 }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>{t("residentsEditor.columns.status")}</TableCell>
                    {showSeats && (
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {t("residentsEditor.columns.callingSeat")}
                          <Tooltip
                            title={t("residentsEditor.columns.callingSeatHint")}
                          >
                            <VideocamIcon
                              fontSize="inherit"
                              color="action"
                              sx={{ fontSize: 14 }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {t("residentsEditor.columns.callsAllowed")}
                        <Tooltip
                          title={t("residentsEditor.columns.callsAllowedHint")}
                        >
                          <VideocamOffIcon
                            fontSize="inherit"
                            color="action"
                            sx={{ fontSize: 14 }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {t("residentsEditor.columns.admitted")}
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roster.map((occ) => (
                    <TableRow
                      key={occ.id}
                      data-testid={`roster-row-${occ.roomId}`}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="fontWeightMedium"
                        >
                          {roomLabel(occ.roomId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {occ.doorSignDisplayName ? (
                          <Chip size="small" label={occ.doorSignDisplayName} />
                        ) : (
                          <Tooltip title={t("residentsEditor.hiddenByPolicy")}>
                            <Typography variant="body2" color="text.secondary">
                              {NO_VALUE}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      {showIdentity && (
                        <TableCell data-testid={`identity-${occ.roomId}`}>
                          {revealed[occ.personRef] ? (
                            <Stack spacing={0.25}>
                              <Typography
                                variant="body2"
                                fontWeight="fontWeightMedium"
                              >
                                {revealed[occ.personRef].name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {identityDetails(revealed[occ.personRef])}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                sx={{
                                  alignSelf: "flex-start",
                                  minWidth: 0,
                                  px: 0,
                                }}
                                onClick={() => hide(occ.personRef)}
                              >
                                {t("residentsEditor.hide")}
                              </Button>
                            </Stack>
                          ) : (
                            <Tooltip title={t("residentsEditor.revealHint")}>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    revealing === occ.personRef ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <VisibilityIcon />
                                    )
                                  }
                                  onClick={() => reveal(occ)}
                                  disabled={revealing === occ.personRef}
                                  data-testid={`reveal-${occ.roomId}`}
                                >
                                  {t("residentsEditor.showIdentity")}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <PermissionGate
                          resource="residents"
                          level="edit"
                          place={place}
                          deniedProps={{ disabled: true }}
                        >
                          <Select
                            size="small"
                            value={occ.doorSignStatus}
                            onChange={(e) =>
                              setStatus(occ, e.target.value as DoorSignStatus)
                            }
                            disabled={busy}
                            data-testid={`status-${occ.roomId}`}
                            sx={{ minWidth: 160 }}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <MenuItem key={status} value={status}>
                                {statusLabel(t, status)}
                              </MenuItem>
                            ))}
                          </Select>
                        </PermissionGate>
                      </TableCell>
                      {showSeats && (
                        <TableCell data-testid={`calling-seat-${occ.roomId}`}>
                          <SeatCell
                            roomId={occ.roomId}
                            seat={seats[occ.personRef] ?? null}
                            summary={seatSummary}
                            busy={seatBusy === occ.personRef}
                            mayChange={
                              seats[occ.personRef]?.source === "family"
                                ? mayAdministrateCalls
                                : mayEditCalls
                            }
                            onChange={(assign) =>
                              void setSeatAssigned(occ, assign)
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell data-testid={`video-calls-${occ.roomId}`}>
                        <Tooltip
                          title={
                            occ.videoCallsDisabled
                              ? t("residentsEditor.videoCallsOffTooltip")
                              : t("residentsEditor.videoCallsOnTooltip")
                          }
                        >
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                color={
                                  occ.videoCallsDisabled ? "error" : "success"
                                }
                                checked={!occ.videoCallsDisabled}
                                onChange={(e) =>
                                  setVideoCallsDisabled(occ, !e.target.checked)
                                }
                                disabled={
                                  busy || !mayEditResidents || !mayEditCalls
                                }
                                data-testid={`video-calls-switch-${occ.roomId}`}
                              />
                            }
                            label={
                              occ.videoCallsDisabled ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  icon={<VideocamOffIcon />}
                                  label={t("residentsEditor.off")}
                                  data-testid={`video-calls-off-${occ.roomId}`}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("residentsEditor.on")}
                                </Typography>
                              )
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmt.dateTime(occ.admittedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          {canManageFamily &&
                            mayViewCalls &&
                            (() => {
                              const counts = familyCounts[occ.personRef];
                              const pending = counts?.pending ?? 0;
                              return (
                                <Tooltip
                                  title={
                                    pending > 0
                                      ? t("residentsEditor.familyPending", {
                                          count: pending,
                                        })
                                      : t("residentsEditor.familyHint")
                                  }
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color={pending > 0 ? "warning" : "primary"}
                                    startIcon={<FamilyIcon />}
                                    onClick={() => setFamilyFor(occ)}
                                    disabled={busy}
                                    data-testid={`family-contacts-${occ.roomId}`}
                                  >
                                    {familyButtonLabel(counts)}
                                  </Button>
                                </Tooltip>
                              );
                            })()}
                          <PermissionGate
                            resource="residents/photos"
                            level="view"
                            place={place}
                          >
                            <Tooltip title={t("residentsEditor.photosHint")}>
                              <Button
                                size="small"
                                variant="outlined"
                                color={
                                  mailboxes[occ.personRef]?.active
                                    ? "success"
                                    : "primary"
                                }
                                startIcon={<PhotoCameraIcon />}
                                onClick={() => setPhotosFor(occ)}
                                disabled={busy}
                                data-testid={`resident-photos-${occ.roomId}`}
                              >
                                {mailboxes[occ.personRef]?.active
                                  ? t("residentsEditor.photosOn")
                                  : t("residentsEditor.photos")}
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate
                            resource="residents"
                            level="view"
                            place={place}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<MedicalIcon />}
                              onClick={() => setProfileFor(occ)}
                              disabled={busy}
                              data-testid={`care-profile-${occ.roomId}`}
                            >
                              {t("residentsEditor.careProfile")}
                            </Button>
                          </PermissionGate>
                          <PermissionGate
                            resource="residents"
                            level="edit"
                            place={place}
                          >
                            <Button
                              size="small"
                              color="error"
                              startIcon={<LogoutIcon />}
                              onClick={() => discharge(occ)}
                              disabled={busy}
                              data-testid={`discharge-${occ.roomId}`}
                            >
                              {t("residentsEditor.discharge")}
                            </Button>
                          </PermissionGate>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Care-profile editor (clinical whiteboard) */}
      {profileFor && (
        <CareProfileDialog
          open={!!profileFor}
          premiseId={premiseId}
          personRef={profileFor.personRef}
          roomLabel={roomLabel(profileFor.roomId)}
          onClose={() => setProfileFor(null)}
          onSaved={() =>
            toast.success(
              t("residentsEditor.profileSaved", {
                room: roomLabel(profileFor.roomId),
              })
            )
          }
        />
      )}

      {/* Family photos - person-scoped, closed with the stay. */}
      {photosFor && (
        <Dialog
          open={!!photosFor}
          onClose={() => setPhotosFor(null)}
          maxWidth="sm"
          fullWidth
          data-testid="resident-photos-dialog"
        >
          <DialogTitle>
            {t("residentsEditor.familyPhotosTitle", {
              room: roomLabel(photosFor.roomId),
            })}
          </DialogTitle>
          <DialogContent>
            <ResidentPhotoMailboxPanel
              premiseId={premiseId}
              personRef={photosFor.personRef}
              existing={mailboxes[photosFor.personRef] ?? null}
              onMailboxChange={(mb) => {
                const ref = photosFor.personRef;
                setMailboxes((prev) => {
                  const next = { ...prev };
                  if (mb) next[ref] = mb;
                  else delete next[ref];
                  return next;
                });
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPhotosFor(null)}>
              {t("common:buttons.close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Family contacts (VC-11) - person-scoped, revoked with the stay. */}
      {familyFor && (
        <FamilyContactsDialog
          open={!!familyFor}
          premiseId={premiseId}
          personRef={familyFor.personRef}
          roomLabel={roomLabel(familyFor.roomId)}
          defaultTz={premiseTimezone || "Europe/Stockholm"}
          canViewPhi={canViewPhi}
          onClose={() => setFamilyFor(null)}
          onChanged={() => void refreshFamilyCounts()}
        />
      )}

      {/* Admit dialog */}
      <Dialog
        open={admitOpen}
        onClose={closeAdmit}
        fullWidth
        maxWidth="sm"
        data-testid="admit-dialog"
      >
        <DialogTitle>{t("residentsEditor.admitTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="admit-room">
                {t("residentsEditor.unit")}
              </InputLabel>
              <Select
                labelId="admit-room"
                label={t("residentsEditor.unit")}
                value={admitRoom}
                onChange={(e) => setAdmitRoom(e.target.value)}
                data-testid="admit-room"
              >
                {freeRooms.length === 0 && (
                  <MenuItem value="" disabled>
                    {t("residentsEditor.noFreeUnits")}
                  </MenuItem>
                )}
                {freeRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {roomLabel(r.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t("residentsEditor.residentName")}
              value={admitName}
              onChange={(e) => setAdmitName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ "data-testid": "admit-name" }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                type="date"
                label={t("residentsEditor.dobOptional")}
                value={admitDob}
                onChange={(e) => setAdmitDob(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "admit-dob" }}
              />
              <TextField
                size="small"
                label={t("residentsEditor.mrnOptional")}
                value={admitMrn}
                onChange={(e) => setAdmitMrn(e.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              size="small"
              label={t("residentsEditor.encounterOptional")}
              value={admitEncounter}
              onChange={(e) => setAdmitEncounter(e.target.value)}
              fullWidth
              inputProps={{ "data-testid": "admit-encounter" }}
            />
            <Typography variant="caption" color="text.secondary">
              {t("residentsEditor.admitHelp")}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdmit} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={admit}
            disabled={busy || !admitRoom || !admitName.trim()}
            data-testid="admit-submit"
          >
            {busy ? t("residentsEditor.admitting") : t("residentsEditor.admit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit compliance panel - PHI access leaves a trail; surface it here so
          operators can see who read what without leaving the CMS. */}
      {canViewPhi && mayViewAudit && (
        <CollapsiblePanel
          title={t("residentsEditor.audit.title")}
          defaultExpanded={false}
          testId="care-audit-panel"
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <TextField
                size="small"
                type="date"
                label={t("residentsEditor.audit.from")}
                value={auditFrom}
                onChange={(e) => setAuditFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "audit-from" }}
              />
              <TextField
                size="small"
                type="date"
                label={t("residentsEditor.audit.to")}
                value={auditTo}
                onChange={(e) => setAuditTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "audit-to" }}
              />
              <TextField
                size="small"
                label={t("residentsEditor.audit.resident")}
                placeholder={t("residentsEditor.audit.allResidents")}
                value={auditSubject}
                onChange={(e) => setAuditSubject(e.target.value)}
                inputProps={{ "data-testid": "audit-subject" }}
                sx={{ minWidth: 220 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<GavelIcon />}
                onClick={() => void loadAudit(auditFilter)}
                disabled={auditLoading}
                data-testid="audit-load"
              >
                {audit === null
                  ? t("residentsEditor.audit.load")
                  : t("common:buttons.refresh")}
              </Button>
              <Button
                size="small"
                variant="contained"
                component="a"
                href={foyerApi.careAuditExportUrl(premiseId, auditFilter)}
                data-testid="audit-export"
              >
                {t("common:buttons.exportCsv")}
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={auditPhiOnly}
                    onChange={(e) => {
                      setAuditPhiOnly(e.target.checked);
                      if (audit !== null) {
                        void loadAudit({
                          ...auditFilter,
                          phiOnly: e.target.checked,
                        });
                      }
                    }}
                    data-testid="audit-phi-only"
                  />
                }
                label={t("residentsEditor.audit.phiOnly")}
              />
              {auditLoading && <CircularProgress size={16} />}
            </Stack>

            {auditError && <Alert severity="error">{auditError}</Alert>}

            {/* Never let a capped page pass for a complete one - the user is
                about to hand this to an inspector. */}
            {auditTruncated && (
              <Alert severity="warning" data-testid="audit-truncated">
                {t("residentsEditor.audit.truncated")}
              </Alert>
            )}

            {/* Tamper-evidence verdict. Loud when broken, quiet when intact. */}
            {auditChain &&
              (auditChain.ok ? (
                <Alert severity="success" data-testid="audit-chain-ok">
                  {t("residentsEditor.audit.chainOk", {
                    count: auditChain.checked,
                    head: auditChain.headSeq,
                    hash: auditChain.headHash.slice(0, 16),
                  })}
                </Alert>
              ) : auditChain.breakAt?.reason === "reorder_window_exceeded" ? (
                // NOT a tamper finding: the verifier could not put the trail
                // back into chain order, so it stopped rather than guess. Saying
                // "this log has been altered" here would send a compliance
                // officer chasing a defect in our reader.
                <Alert
                  severity="warning"
                  data-testid="audit-chain-inconclusive"
                >
                  {t("residentsEditor.audit.chainInconclusive", {
                    seq: auditChain.breakAt?.seq,
                  })}
                </Alert>
              ) : (
                <Alert severity="error" data-testid="audit-chain-broken">
                  {t("residentsEditor.audit.chainBroken", {
                    seq: auditChain.breakAt?.seq,
                    reason: auditChain.breakAt?.reason,
                  })}
                </Alert>
              ))}

            {/* What the verdict above does not reach. Rows written before the
                hash chain shipped carry no digest, so they can be neither
                confirmed nor faulted - and a green "integrity verified" that
                quietly covered them would overclaim in the one place this
                product must not: the answer a care provider gives an
                inspector. Stated separately rather than folded into the
                verdict, because it is not a finding. */}
            {auditChain !== null && auditChain.unchained > 0 && (
              <Alert severity="info" data-testid="audit-chain-unchained">
                {t("residentsEditor.audit.unchained", {
                  count: auditChain.unchained,
                })}
              </Alert>
            )}

            {audit !== null &&
              (audit.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("residentsEditor.audit.empty")}
                </Typography>
              ) : (
                <Table size="small" data-testid="audit-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("residentsEditor.audit.columns.when")}
                      </TableCell>
                      <TableCell>
                        {t("residentsEditor.audit.columns.actor")}
                      </TableCell>
                      <TableCell>
                        {t("residentsEditor.audit.columns.action")}
                      </TableCell>
                      <TableCell>
                        {t("residentsEditor.audit.columns.resource")}
                      </TableCell>
                      <TableCell>
                        {t("residentsEditor.audit.columns.outcome")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="caption">
                            {fmt.dateTime(row.at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.actor}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Typography variant="body2">
                              {row.action}
                            </Typography>
                            {row.phi && (
                              <Chip
                                size="small"
                                color="warning"
                                label={PHI_CHIP}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.resourceId
                              ? t("residentsEditor.audit.resourceLine", {
                                  type: row.resourceType,
                                  id: row.resourceId,
                                })
                              : row.resourceType}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              row.outcome === "success"
                                ? "success"
                                : row.outcome === "denied"
                                  ? "error"
                                  : "default"
                            }
                            label={row.outcome}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ))}
          </Stack>
        </CollapsiblePanel>
      )}
    </Stack>
  );
}

/** The pool line above the roster: bought / assigned / left. */
function poolCopy(t: Translate, s: VideoCallSeatSummary): string {
  if (s.unlimited) {
    return t("care:residentsEditor.pool.unlimited");
  }
  if (s.poolSeats === 0) {
    return s.familySeats > 0
      ? t("care:residentsEditor.pool.noneFamily", { count: s.familySeats })
      : t("care:residentsEditor.pool.none");
  }
  return s.familySeats
    ? t("care:residentsEditor.pool.summaryWithFamily", {
        assigned: s.assignedFromPool,
        total: s.poolSeats,
        free: s.freeSeats,
        family: s.familySeats,
      })
    : t("care:residentsEditor.pool.summary", {
        assigned: s.assignedFromPool,
        total: s.poolSeats,
        free: s.freeSeats,
      });
}

/**
 * One resident's seat control.
 *
 * Three shapes, because the switch is only honest in one of them:
 *   - **Site license** - entitlement comes from the pool being unlimited, so a
 *     per-resident seat would change nothing. A switch here would be a control
 *     that does not control anything; show the fact instead.
 *   - **Holds a seat** - on, plus WHO funds it. A family seat is not the
 *     operator's to give back (VC2-6), so it must never look like a plain
 *     operator toggle.
 *   - **No seat** - on if the pool has one to draw, greyed with the reason
 *     when it does not. Blocking here rather than letting the click 409 keeps
 *     "the pool is full" a fact the operator reads, not an error they hit.
 */
function SeatCell({
  roomId,
  seat,
  summary,
  busy,
  mayChange,
  onChange,
}: {
  roomId: string;
  seat: VideoCallSeat | null;
  summary: VideoCallSeatSummary | null;
  busy: boolean;
  mayChange: boolean;
  onChange: (assign: boolean) => void;
}) {
  const { t } = useTranslation("care");
  if (summary?.unlimited) {
    return (
      <Tooltip title={t("residentsEditor.seatCell.siteLicenseHint")}>
        <Chip
          size="small"
          variant="outlined"
          color="success"
          label={t("residentsEditor.seatCell.siteLicense")}
          data-testid={`calling-seat-unlimited-${roomId}`}
        />
      </Tooltip>
    );
  }

  const family = seat?.source === "family";
  const poolEmpty = !!summary && summary.poolSeats === 0;
  const poolFull = !!summary && !summary.hasFreeSeats;
  // A resident who already holds a seat can always be switched OFF - the block
  // is on drawing a NEW seat, never on giving one back.
  const blocked = !seat && (poolEmpty || poolFull);

  const title = seat
    ? family
      ? t("residentsEditor.seatCell.familyFundedHint")
      : t("residentsEditor.seatCell.operatorHint")
    : poolEmpty
      ? t("residentsEditor.seatCell.poolEmptyHint")
      : poolFull
        ? t("residentsEditor.seatCell.poolFullHint", {
            count: summary?.poolSeats,
          })
        : t("residentsEditor.seatCell.assignHint");

  return (
    <Tooltip title={title}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          busy ? (
            <Box
              sx={{
                width: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={14} />
            </Box>
          ) : (
            <Switch
              size="small"
              color={family ? "info" : "success"}
              checked={!!seat}
              onChange={(e) => onChange(e.target.checked)}
              disabled={blocked || !mayChange}
              // On the INPUT, not the wrapper: `disabled`/`checked` live there,
              // so a test asserting either is asserting the real control.
              inputProps={
                {
                  "data-testid": `calling-seat-switch-${roomId}`,
                } as Record<string, string>
              }
            />
          )
        }
        label={
          seat ? (
            <Chip
              size="small"
              variant="outlined"
              color={family ? "info" : "success"}
              label={
                family
                  ? t("residentsEditor.seatCell.familyFunded")
                  : t("residentsEditor.seatCell.operator")
              }
              data-testid={`calling-seat-source-${roomId}`}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {blocked
                ? poolEmpty
                  ? t("residentsEditor.seatCell.noSeatsBought")
                  : t("residentsEditor.seatCell.poolFull")
                : t("residentsEditor.seatCell.none")}
            </Typography>
          )
        }
      />
    </Tooltip>
  );
}
